import { FC, useCallback, useMemo } from "react";
import { blue, deepPurple } from "@mui/material/colors";
import { AxisLabelsFormatterContextObject, Point } from "highcharts";
import Stack from "@mui/material/Stack";
import { SxProps } from "@mui/system";
import { Theme } from "@mui/material";
import { HighchartsProvider } from "@/storybook/c8r-highcharts/common/components/HighchartsProvider";
import { MultiTypeHighcharts } from "@/storybook/c8r-highcharts/MultiTypeHighcharts";
import { HighchartsChartDataType } from "@/storybook/c8r-highcharts/common/utils/types/common";
import { CommitmentDateGranularDataPoint } from "@/services/cloudchipr.api";

interface CommitmentsHighChartProps {
  loading: boolean;
  labelFormatter?(value: string): string;
  dataGroupedByDate?: Record<string, CommitmentDateGranularDataPoint>;
  ChartHeader?: FC;
  maxHeight?: number;
  tooltipMarkupGenerator(point: Point): string;
  chartSx?: SxProps<Theme>;
}

export const CommitmentsHighChart: FC<CommitmentsHighChartProps> = ({
  dataGroupedByDate,
  labelFormatter,
  loading,
  ChartHeader,
  maxHeight,
  tooltipMarkupGenerator,
  chartSx,
}) => {
  const chartData: HighchartsChartDataType | null = useMemo(() => {
    if (!dataGroupedByDate) return null;

    const sortedPoints = Object.values(dataGroupedByDate).sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );

    const categories: string[] = [];
    const coverageData: { y: number | null }[] = [];
    const utilizationData: { y: number | null }[] = [];

    sortedPoints.forEach((point) => {
      categories.push(point.date);
      coverageData.push({ y: point.coverage?.percent ?? null });
      utilizationData.push({ y: point.utilization?.percent ?? null });
    });

    return {
      categories,
      series: [
        {
          name: "Utilization",
          data: utilizationData,
          color: deepPurple[300],
        },
        {
          name: "Coverage",
          data: coverageData,
          color: blue[400],
        },
      ],
    };
  }, [dataGroupedByDate]);

  const xAxisLabelsFormatterLocal = useCallback(
    (params: AxisLabelsFormatterContextObject) => {
      return labelFormatter?.(params?.value?.toString() ?? "") ?? "";
    },
    [labelFormatter],
  );

  return (
    <Stack>
      {ChartHeader && <ChartHeader />}
      <HighchartsProvider>
        <MultiTypeHighcharts
          chartScale="linear"
          chartType="area"
          chartData={chartData}
          showDataLabels
          loading={loading}
          yAxisLabelsFormatter={yAxisLabelsFormatter}
          xAxisLabelsFormatter={xAxisLabelsFormatterLocal}
          noDataMessage="No data "
          tooltipMarkupGenerator={tooltipMarkupGenerator}
          maxHeight={maxHeight}
          yaxisConfigs={{ tickPositions: [0, 50, 100] }}
          tooltipOptions={{ shared: true }}
          chartSx={chartSx}
        />
      </HighchartsProvider>
    </Stack>
  );
};

const yAxisLabelsFormatter = (params: AxisLabelsFormatterContextObject) => {
  return `${params.value}%`;
};
